import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, Sparkles, ArrowRight, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [trialDays, setTrialDays] = useState(14);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/billing/plans").then((r) => {
      setPlans(r.data.plans);
      setTrialDays(r.data.trial_days);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Selecione um plano");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/billing/checkout", {
        plan: selected,
        interval: "month",
        origin_url: window.location.origin,
      });
      if (r.data.checkout_url) {
        toast.success("Redirecionando para o pagamento...");
        window.location.href = r.data.checkout_url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no checkout");
    } finally {
      setLoading(false);
    }
  };

  if (!plans) {
    return (
      <div className="space-y-4 max-w-6xl mx-auto p-8">
        <Skeleton className="h-12 w-1/2" />
        <div className="grid md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-96" />
          ))}
        </div>
      </div>
    );
  }

  const state = user?.plan_state || {};
  const isExpired = state.plan_status === "expired" || state.plan_status === "canceled";

  return (
    <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-8" data-testid="pricing-page">
      <header className="text-center max-w-2xl mx-auto">
        {isExpired ? (
          <>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-300 text-xs uppercase tracking-wider mb-4">
              <Lock className="w-3 h-3" /> Trial expirado
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
              Continue usando o <span className="text-primary">SmartStockAI</span>
            </h1>
            <p className="text-muted-foreground mt-4">
              Seu trial gratuito acabou. Escolha o plano ideal para sua operação e mantenha tudo funcionando.
            </p>
          </>
        ) : state.plan_status === "trial" ? (
          <>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/40 bg-primary/10 text-primary text-xs uppercase tracking-wider mb-4">
              <Sparkles className="w-3 h-3" />{" "}
              {state.trial_days_left} dias de trial restantes
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
              Escolha seu plano
            </h1>
            <p className="text-muted-foreground mt-4">
              Continue com acesso completo após o trial. Cancele quando quiser.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
              Atualize seu plano
            </h1>
            <p className="text-muted-foreground mt-4">
              Plano atual: <strong className="text-foreground">{state.plan_chosen}</strong>.
              Faça upgrade para desbloquear funcionalidades avançadas.
            </p>
          </>
        )}
      </header>

      {/* Plan cards */}
      <div className="grid md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const sel = selected === p.id;
          return (
            <button
              key={p.id}
              type="button"
              data-testid={`plan-card-${p.id}`}
              onClick={() => setSelected(p.id)}
              className={`text-left rounded-2xl border bg-card p-7 transition-all relative ${
                sel
                  ? "border-primary ring-2 ring-primary/40 glow-coral"
                  : "border-border hover:border-primary/40"
              } ${p.popular ? "md:scale-[1.02]" : ""}`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-7 bg-primary text-white text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-md">
                  Mais popular
                </span>
              )}
              <div className="font-display text-2xl font-bold">{p.name}</div>
              <p className="text-xs text-muted-foreground mt-1">{p.ideal_for}</p>
              <div className="my-5">
                <span className="font-display text-4xl font-bold">
                  R$ {p.price.toLocaleString("pt-BR")}
                </span>
                <span className="text-muted-foreground text-sm">/mês</span>
              </div>
              <p className="text-xs uppercase tracking-wider text-primary mb-3">
                {p.tagline}
              </p>
              <ul className="space-y-2 text-sm">
                {p.features.map((f) => {
                  const soon = f.includes("(em breve)");
                  const label = soon ? f.replace(/\s*\(em breve\)\s*/i, "") : f;
                  return (
                    <li key={f} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${soon ? "text-muted-foreground/60" : "text-primary"}`} />
                      <span className={soon ? "text-foreground/60" : "text-foreground/85"}>
                        {label}
                        {soon && (
                          <span className="ml-1.5 inline-flex items-center text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border border-primary/30 bg-primary/5 text-primary align-middle">
                            Em breve
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {sel && (
                <div className="mt-5 flex items-center gap-2 text-primary font-medium text-sm">
                  <Check className="w-4 h-4" /> Selecionado
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Checkout panel */}
      {selected && (
        <form
          onSubmit={submit}
          className="rounded-2xl border border-border bg-card p-7 max-w-2xl mx-auto space-y-6"
          data-testid="checkout-form"
        >
          <div>
            <h3 className="font-display text-2xl font-bold tracking-tight">Finalizar assinatura</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Plano <strong className="text-foreground">{plans.find((p) => p.id === selected)?.name}</strong> — R${" "}
              {plans.find((p) => p.id === selected)?.price.toLocaleString("pt-BR")}/mês
            </p>
          </div>

          <div className="grid gap-4 p-5 rounded-xl border border-border bg-secondary/30">
            <div className="flex items-start gap-3">
              <Lock className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div className="text-sm text-muted-foreground">
                Pagamento seguro processado pelo <strong className="text-foreground">Stripe</strong>. Você preenche o cartão e dados fiscais na próxima tela — cancele a qualquer momento pelo seu perfil.
              </div>
            </div>
          </div>

          <Button
            data-testid="checkout-submit"
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-primary hover:bg-primary/90"
          >
            {loading ? "Redirecionando..." : "Continuar para pagamento"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center">
            <ShieldCheck className="w-3.5 h-3.5 text-primary" />
            Powered by Stripe · PCI DSS Level 1 · Cancele a qualquer momento
          </div>
        </form>
      )}
    </div>
  );
}
